import { Color, Spacing } from '@persona-id/design-tokens';
import { IdFrontIcon } from '@persona-id/design-tokens/icons/24px';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import InquiryStatusSticker from 'dashboard/components/InquiryStatusSticker';
import Pii from 'dashboard/components/Pii';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, {
  HelpTextWithButton,
  TableActionTD,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const InquiriesTableContainer = styled('div')`
  margin-bottom: ${Spacing[2]};
`;

interface InquiriesTableProps {
  dataTest?: string;
  inquiries: Inquiry[];
  sortParams: SortParams;
  canSort: boolean;
  environment: Environment;
  hasUsedFilters: boolean;
  loading: boolean;
  handleSortUpdate: (sortParams: SortParams) => void;
}

const InquiriesTable = ({
  dataTest,
  inquiries = [],
  sortParams,
  canSort,
  environment,
  hasUsedFilters,
  loading,
  handleSortUpdate,
}: InquiriesTableProps) => {
  const handleColumnHeaderClick = (columnName: string) => {
    if (!canSort) {
      return;
    }

    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const hasPreviewPermission = usePermission(Permission.InquiryTemplateView);

  const inquiryRows = inquiries.map((inquiry, key) => (
    <InquiryTableRow
      key={key}
      inquiry={inquiry}
      previewEnabled={
        environment.type === ApiModel.EnvironmentSandbox &&
        inquiry.relationships?.['inquiry-template']?.data?.id != null &&
        hasPreviewPermission
      }
    />
  ));

  return (
    <InquiriesTableContainer data-test={dataTest}>
      <TableLayout
        isLoading={loading}
        isEmpty={inquiries.length === 0}
        showHeader={hasUsedFilters}
        nullState={
          <TableNullState hasUsedFilters={hasUsedFilters} dataTestBlock='inquiries-view'>
            <IdFrontIcon size={64} color={Color.BorderIconSecondary} />
            <Spacer height={3} />
            <div>
              View and verify collected information. Create an inquiry, follow the steps, then find
              it here.
            </div>
            <Spacer height={5} />
            <a
              href='https://support.withpersona.com/hc/en-us/articles/360055726494-Inquiry-Model-and-other-Key-Concepts'
              target='_blank'
              rel='noreferrer'
            >
              Learn more about inquiries
            </a>
          </TableNullState>
        }
        headerTR={
          <>
            <TableTH
              onClick={() => handleColumnHeaderClick('name-full')}
              canSort={canSort}
              isSorted={sortParams.sort === 'name-full'}
              isDescending={sortParams.shouldSortDescending}
              id='inquiries-table__name-full-header'
            >
              Name
            </TableTH>
            <TableTH helpText='An Inquiry ID is a unique ID generated by Persona for each inquiry.'>
              Inquiry ID
            </TableTH>
            <TableTH>Reference ID</TableTH>
            <TableTH
              onClick={() => handleColumnHeaderClick('created_at')}
              canSort={canSort}
              isSorted={sortParams.sort === 'created_at'}
              isDescending={sortParams.shouldSortDescending}
              id='inquiries-table__created-at-header'
            >
              {`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
            </TableTH>
            <TableTH
              onClick={() => handleColumnHeaderClick('status')}
              canSort={canSort}
              isSorted={sortParams.sort === 'status'}
              isDescending={sortParams.shouldSortDescending}
              id='inquiries-table__status-header'
              helpText={
                <HelpTextWithButton to='https://docs.withpersona.com/docs/models-lifecycle#inquiry-lifecycle'>
                  Inquiry statuses indicate key events in the inquiry lifecycle, tracking where
                  individuals are in the process as different actions are taken.
                </HelpTextWithButton>
              }
            >
              Status
            </TableTH>
            <TableTH isSticky>{/* Actions column */}</TableTH>
          </>
        }
      >
        {inquiryRows}
      </TableLayout>
    </InquiriesTableContainer>
  );
};

interface InquiryTableRowProps {
  inquiry: Inquiry;
  previewEnabled: boolean;
}

const InquiryTableRow = ({ inquiry, previewEnabled }: InquiryTableRowProps) => {
  const {
    status,
    'created-at': createdAt,
    'name-full': nameFull,
    'name-full-localized': nameFullLocalized,
    'redacted-at': redactedAt,
    'reference-id': referenceId,
  } = inquiry.attributes;

  return (
    <TableLinkTR
      to={`/dashboard/inquiries/${inquiry.id}`}
      data-test={`inquiry-table-row__${inquiry.id}`}
      id='inquiry-table__row'
    >
      <TableTD>
        <Pii isRedacted={redactedAt != null}>
          {nameFullLocalized ?? nameFull ?? <Paragraph color='disabled'>No name</Paragraph>}
        </Pii>
      </TableTD>
      <TableTD>
        <HoverCopyableField copyValue={inquiry.id} confirmationText='Copied Inquiry ID'>
          <MonoSpan>{inquiry.id}</MonoSpan>
        </HoverCopyableField>
      </TableTD>
      <TableTD>
        <MonoSpan>{referenceId}</MonoSpan>
      </TableTD>
      <TableTD>{DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
      <TableTD>
        <InquiryStatusSticker status={status} />
      </TableTD>
      <TableActionTD>
        {previewEnabled && (
          <ContextMenu
            items={[
              {
                id: 'preview',
                onClick: () => {
                  window.open(`/dashboard/inquiries/${inquiry.id}/preview`, '_blank');
                },
                variant: 'basic' as const,
                label: 'Preview',
              },
            ]}
            renderItem={(item) => (
              <ContextMenu.Item
                key={item.id}
                id={`inquiries-table-row__${inquiry.id}--action-menu-${item.id}`}
                onClick={(e) => {
                  // Needed to avoid triggering row click below
                  e.stopPropagation();
                  e.preventDefault();
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  item.onClick();
                }}
                variant={item.variant}
              >
                {item.label}
              </ContextMenu.Item>
            )}
          />
        )}
      </TableActionTD>
    </TableLinkTR>
  );
};

export default InquiriesTable;
